/**
 * Add-to-wishlist modal (list picker), repurposed for the rebuild (#89):
 * rows render from GET <PROXY_URL>/state and writes go through the new
 * app proxy — no Bearer token, no legacy Rails API, no metafield-driven
 * Liquid rows. A heart click opens it (see wishlist-heart.js), pointing
 * dataset.productId / dataset.variantId at the product to act on.
 *
 * Wire contract (numeric-string ids):
 *   GET  <PROXY_URL>/state         -> { lists: [{ id, title, itemCount, previewImage: { url, altText } | null, items: [{ id, variantId, productId, quantity }] }] }
 *   POST <PROXY_URL>/items         -> { listId, item, created }        body { variantId, listId }
 *   POST <PROXY_URL>/items/remove  -> { removed }                      body { listId, itemId }
 *   POST <PROXY_URL>/lists         -> { created, slug, listId }        body { title }
 *
 * Hearts stay in sync through the document-level wishlist-item-added /
 * wishlist-item-removed events; after a remove that leaves the variant in
 * another list, a follow-up added event re-points them at the surviving
 * membership.
 */

// Seeded by bao-head-wishlist.liquid from the bao_rebuild_proxy_url theme
// setting; the literal covers pages the seed has not run on.
const PROXY_URL = window.BAO?.wishlist?.proxyBase || '/apps/wishlist'

class WishlistModal extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.locked = false

    // The lists from the last render, kept for the row rendering
    this.lists = []

    // Monotonic render token: a slow /state response from an earlier open
    // must not overwrite the rows of a later one
    this.renderSeq = 0

    // The render token whose fresh /state response has painted the rows —
    // a cached paint resolving after it must not overwrite it
    this.freshSeq = 0
  }

  setupListeners () {
    super.setupListeners()

    this.onCloseClick = this.onCloseClick.bind(this)
    this.onTriggerClick = this.onTriggerClick.bind(this)
    this.onCreateFormSubmit = this.onCreateFormSubmit.bind(this)
    this.onCreateTriggerClick = this.onCreateTriggerClick.bind(this)

    this.listeners.add(document.body, 'click', this.onTriggerClick)
    this.listeners.add(this, 'click', this.onCloseClick)
    this.listeners.add(
      this.els['create-form'].element,
      'submit',
      this.onCreateFormSubmit,
    )
    this.listeners.add(
      this.els['create-trigger'].element,
      'click',
      this.onCreateTriggerClick,
    )
  }

  async onTriggerClick (event) {
    const target = event.target.closest(this.els?.trigger?.selector)
    if (!target) return

    if (this.locked) return

    this.locked = true

    const context = this.snapshotContext()

    target.setAttribute('aria-busy', 'true')

    try {
      if (target.getAttribute('data-added') === 'true') {
        await this.removeFromWishlist(
          target.dataset.listId,
          target.getAttribute('data-item-id'),
          context,
        )
      } else {
        await this.addToWishlist(target.dataset.listId, context)
      }
    } catch (error) {
      this.log('wishlist-modal action failed', error)

      // The rows the customer acted on may be stale (the request can fail
      // because the server moved on, e.g. a proxied response timing out
      // after the write landed) — resync them with reality
      this.render()
    } finally {
      target.setAttribute('aria-busy', 'false')

      this.locked = false
    }
  }

  async onCreateFormSubmit (event) {
    event.preventDefault()

    if (this.locked) return

    this.locked = true

    const context = this.snapshotContext()

    event.target.setAttribute('aria-busy', 'true')

    try {
      const title = new FormData(event.target).get('name')

      const response = await fetch(`${PROXY_URL}/lists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      })

      if (!response.ok) {
        throw new Error(`Wishlist list create failed: ${response.status}`)
      }

      const payload = await response.json()

      await this.addToWishlist(payload.listId, context)

      event.target.reset()
    } catch (error) {
      this.log('wishlist-modal create failed', error)
    } finally {
      event.target.setAttribute('aria-busy', 'false')

      this.locked = false
    }
  }

  onCreateTriggerClick (event) {
    if (
      !event.target.closest(this.els['create-trigger-text'].selector) &&
      !event.target.closest(this.els['create-trigger-icon'].selector)
    ) {
      return
    }
    this.els['create-trigger-text'].element.setAttribute('aria-hidden', 'true')
    this.els['create-form'].element.setAttribute('aria-hidden', 'false')
  }

  /**
   * The product/variant the modal is pointed at, captured the moment an
   * action starts: another heart can re-point the singleton modal while a
   * request is in flight, and the request and the events announcing it have
   * to describe the product the customer actually acted on.
   */
  snapshotContext () {
    return {
      variantId: String(this.variantId),
      productId: this.productId,
    }
  }

  async addToWishlist (listId, { variantId, productId }) {
    const response = await fetch(`${PROXY_URL}/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variantId, listId }),
    })

    if (!response.ok) {
      throw new Error(`Wishlist add failed: ${response.status}`)
    }

    const payload = await response.json()

    document.dispatchEvent(
      new CustomEvent('wishlist-item-added', {
        detail: {
          productId,
          variantId,
          listId: payload.listId,
          itemId: payload.item.id,
          item: payload.item,
        },
      }),
    )

    this.absorbAdd(payload.listId, payload.item)
    this.close()
  }

  /**
   * Fold the landed add into the local rows and repaint, then the caller
   * closes. Closing after the add is the legacy shape (origin/master
   * `addToWishlist` closed then re-rendered in the background); Dan removed
   * it on 05.09.2026 and restored it on 11.09.2026 (#525) so this and the
   * add-to-registry modal behave the same way. The repaint is for the next
   * open: count up, Add flipped to Remove, thumbnail painted.
   */
  absorbAdd (listId, item) {
    const list = this.lists.find(l => String(l.id) === String(listId))

    // A list these rows have never painted — the inline create's, always —
    // has no row to fold into, so read the lists back instead of leaving the
    // rows (and the empty-state copy) at what they were before the create
    // (#625, Rob's duplicate "List 1").
    if (!list) {
      this.refreshRows()
      return
    }

    const hadImage = Boolean(list.previewImage)
    const member = list.items.some(i => String(i.id) === String(item.id))

    if (!member) {
      list.items.push(item)
      list.itemCount += 1
    }

    this.renderRows()

    // An add into an empty list makes that item its first, and previewImage
    // is the first item's image server-side (#327/#346), so the row's grey
    // placeholder is now wrong. The add response carries no image, so read
    // the lists back and repaint — the Copy to sheet's rule
    // (wishlist-copy-to.js applyCopy).
    if (!hadImage && !member) this.refreshRows()
  }

  /**
   * The mirror of absorbAdd. The preview can change on a remove too — the
   * removed item may have BEEN the list's first — so a list that had an
   * image is always read back.
   */
  absorbRemove (listId, itemId) {
    const list = this.lists.find(l => String(l.id) === String(listId))
    if (!list) return

    const before = list.items.length

    list.items = list.items.filter(i => String(i.id) !== String(itemId))

    if (list.items.length !== before) list.itemCount -= 1

    this.renderRows()
  }

  /**
   * A fresh read painted over the open rows. Unlike `render()` it never
   * paints from the cache first: the rows already carry the change and the
   * cached state predates it. Silent on failure — the write landed, and the
   * only thing at stake is a preview image.
   */
  async refreshRows () {
    const seq = this.renderSeq

    try {
      const lists = await this.fetchLists()

      if (!lists || seq !== this.renderSeq) return

      this.lists = lists
      this.freshSeq = seq

      this.renderRows()
    } catch (error) {
      this.log('wishlist-modal refresh failed', error)
    }
  }

  async removeFromWishlist (listId, itemId, { variantId, productId }) {
    const response = await fetch(`${PROXY_URL}/items/remove`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ listId, itemId }),
    })

    if (!response.ok) {
      throw new Error(`Wishlist remove failed: ${response.status}`)
    }

    document.dispatchEvent(
      new CustomEvent('wishlist-item-removed', {
        detail: {
          productId,
          variantId,
          listId,
          itemId,
        },
      }),
    )

    this.absorbRemove(listId, itemId)

    // Hearts went inactive on the removed event; when the variant survives in
    // another list, re-point them at that membership so they stay active.
    // Derived from a fresh state read, not the open-time snapshot — another
    // tab may have removed the other membership while this modal sat open.
    // The same read repaints the rows, so a removal that took a list's first
    // item cannot leave its old thumbnail behind.
    const lists = await this.fetchLists()
    const survivor = this.survivingMembership(lists, variantId, itemId)

    if (survivor) {
      document.dispatchEvent(
        new CustomEvent('wishlist-item-added', {
          detail: {
            productId,
            variantId,
            listId: survivor.listId,
            itemId: survivor.itemId,
            item: survivor.item,
          },
        }),
      )
    }

    if (lists) {
      this.lists = lists

      this.renderRows()
    }
  }

  async fetchLists () {
    try {
      const response = await fetch(`${PROXY_URL}/state`, {
        headers: { Accept: 'application/json' },
      })

      if (!response.ok) return null

      return (await response.json()).lists
    } catch {
      return null
    }
  }

  survivingMembership (lists, variantId, removedItemId) {
    if (!lists) return null

    // The just-removed item is excluded by id in case the read lags the
    // delete — it must not resurrect the membership it just lost
    for (const list of lists) {
      const item = list.items.find(
        i =>
          String(i.variantId) === variantId &&
          String(i.id) !== String(removedItemId),
      )

      if (item) {
        return { listId: list.id, itemId: item.id, item }
      }
    }

    return null
  }

  onCloseClick (event) {
    const target = event.target.closest(this.els?.close?.selector)
    if (!target) return

    this.close()
  }

  open () {
    if (!document.body.classList.contains('util-SelectVariant')) {
      document.body.classList.add('util-SiteOverflowed')
    }

    this.setAttribute('aria-hidden', 'false')

    return this.render()
  }

  close () {
    this.setAttribute('aria-hidden', 'true')
    this.els['create-trigger'].element.setAttribute('aria-hidden', 'false')
    this.els['create-form'].element.setAttribute('aria-hidden', 'true')
    document.body.classList.remove('util-SiteOverflowed')
  }

  /**
   * Fetched fresh on every open: counts and membership move under the modal
   * (other tabs, the lists page, earlier opens), and one small GET per open
   * is cheaper than being wrong. The hearts' shared cache paints the rows
   * first so the modal never opens empty while that read is in flight.
   */
  async render () {
    const seq = ++this.renderSeq

    this.renderFromCache(seq)

    try {
      const response = await fetch(`${PROXY_URL}/state`, {
        headers: { Accept: 'application/json' },
      })

      if (response.status === 401) {
        window.BAO.wishlist.redirectToLogin(`wishlist-${this.variantId}`)
        return
      }

      if (!response.ok) {
        throw new Error(`Wishlist state request failed: ${response.status}`)
      }

      const payload = await response.json()

      if (seq !== this.renderSeq) return

      this.lists = payload.lists
      this.freshSeq = seq

      this.renderRows()
    } catch (error) {
      this.log('wishlist-modal render failed', error)
    }
  }

  /**
   * Stale-while-revalidate: paint the rows from the hearts' shared state
   * cache (wishlist-heart.js keeps it coherent through the item-added /
   * item-removed events) so an open never sits empty waiting on the proxy.
   * Yields to the fresh read on both tokens — a later open (renderSeq) and
   * this open's own fresh response landing first (freshSeq).
   */
  async renderFromCache (seq) {
    let cached

    try {
      cached = await window.BAO.wishlist.cachedState?.()
    } catch {
      return
    }

    if (!cached?.loggedIn) return

    if (seq !== this.renderSeq || this.freshSeq === seq) return

    this.lists = cached.lists

    this.renderRows()
  }

  renderRows () {
    const itemsEl = this.els.items.element
    const template = this.els['item-template'].element
    const emptyEl = this.els?.empty?.element

    if (emptyEl) {
      emptyEl.hidden = this.lists.length > 0
    }

    itemsEl.innerHTML = ''

    for (const list of this.lists) {
      const row = template.content.cloneNode(true)
      const trigger = row.querySelector(
        this.els?.trigger?.selector ?? '[data-wishlist-modal-el="trigger"]',
      )
      const imageContainer = row.querySelector(
        '.lpm-ListPickerItem_ImageContainer',
      )
      const title = row.querySelector(
        '[data-wishlist-modal-el="trigger-title"]',
      )
      const owner = row.querySelector(
        '[data-wishlist-modal-el="trigger-owner"]',
      )
      const addLabel = row.querySelector(
        '[data-wishlist-modal-el="trigger-label"][data-view="add"]',
      )
      const removeLabel = row.querySelector(
        '[data-wishlist-modal-el="trigger-label"][data-view="remove"]',
      )
      const member = list.items.find(
        i => String(i.variantId) === String(this.variantId),
      )

      trigger.dataset.listId = list.id
      title.textContent = `${list.title} (${list.itemCount})`

      // A list shared with edit access names its owner (#880)
      if (owner && list.ownerName) {
        owner.querySelector('.lpm-ListPickerItem_OwnerName').textContent =
          list.ownerName
        owner.hidden = false
      }

      if (imageContainer && list.previewImage) {
        const image = document.createElement('img')
        image.className = 'lpm-ListPicker_Image'
        image.src = list.previewImage.url
        image.alt = list.previewImage.altText ?? ''
        image.loading = 'lazy'
        imageContainer.appendChild(image)
      }

      if (member) {
        trigger.setAttribute('data-added', 'true')
        trigger.setAttribute('data-item-id', member.id)

        addLabel.hidden = true
        removeLabel.hidden = false
      }

      itemsEl.appendChild(row)
    }
  }

  get productId () {
    return this.dataset.productId
  }

  get variantId () {
    return this.dataset.variantId
  }
}

customElements.define('wishlist-modal', WishlistModal)
